import { Navigate, useNavigate } from "react-router-dom";
import { useFetchNearbyRestaurantsQuery, useTypedSelector } from "../store";
import { AddressSuggestion, Restaurant } from "../types";
import Skeleton from "../components/common/Skeleton";
import AdsCarousel from "../components/features/AdsCarousel";

const RestaurantsWrapper = () => {
  const { address } = useTypedSelector((state) => state.session.search);
  if (!address) return <Navigate to="/" />;

  return <Restaurants address={address} />;
};

interface RestaurantsProps {
  address: AddressSuggestion;
}

const Restaurants: React.FC<RestaurantsProps> = ({ address }) => {
  const { search } = useTypedSelector((state) => state.session);
  const { orderType } = search;
  const { data: restaurants, isFetching: isFetchingRestaurants } =
    useFetchNearbyRestaurantsQuery({ q: address.displayName, orderType });

  const containerClasses =
    "w-full max-w-7xl mx-auto gap-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 p-4 my-8 md:my-12 auto-rows-fr";

  if (isFetchingRestaurants) {
    return (
      <div className={containerClasses}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex flex-col gap-3">
            <Skeleton className="h-48 w-full rounded-xl" times={1} />
            <Skeleton className="h-6 w-3/4 rounded" times={1} />
            <Skeleton className="h-4 w-1/2 rounded" times={1} />
          </div>
        ))}
      </div>
    );
  }

  const renderdRestaurants = restaurants?.map((restaurant) => {
    return <RestaurantCard key={restaurant._id} restaurant={restaurant} />;
  });

  return (
    <div className="w-full flex flex-col items-center justify-center min-h-[calc(100vh-theme(spacing.20))]">
      <div className="w-full max-w-7xl mx-auto px-4 mb-8">
        <AdsCarousel address={address.displayName} />
      </div>

      {restaurants && restaurants.length > 0 ? (
        <div className="w-full bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Restaurants à proximité
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {renderdRestaurants}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col items-center justify-center flex-grow py-12 px-4 text-center">
          <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <img
              src="/assets/images/coming-soon.jpeg"
              alt="Coming Soon"
              className="w-full h-auto object-contain mb-6 rounded-lg"
            />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              On arrive bientôt !
            </h3>
            <p className="text-gray-500">
              Nous travaillons dur pour vous apporter les meilleurs restaurants
              de votre quartier.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantsWrapper;

const RestaurantCard: React.FC<{ restaurant: Restaurant }> = ({
  restaurant,
}) => {
  const navigate = useNavigate();
  const isOpen = restaurant.openingDetails.isOpen;

  const handleClick = () => {
    if (!isOpen) return;
    navigate("/restaurants/" + restaurant._id);
  };

  return (
    <div
      onClick={handleClick}
      className={`group flex flex-col rounded-xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300
        ${
          isOpen
            ? "cursor-pointer hover:-translate-y-1"
            : "cursor-not-allowed opacity-75 grayscale-[0.5]"
        }
      `}
    >
      <div className="relative w-full aspect-[16/9] overflow-hidden">
        <img
          src={restaurant.images.bannerPath}
          alt={restaurant.displayName}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
            isOpen ? "" : "opacity-90"
          }`}
        />

        {!isOpen && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-[1px]">
            <span className="bg-white/90 text-black px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
              Fermé
            </span>
          </div>
        )}
        {isOpen && (
          <div className="absolute top-3 right-3 bg-white/90 text-green-700 px-2 py-1 rounded-md text-xs font-bold shadow-sm flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            Ouvert
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-1">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-primary transition-colors">
            {restaurant.displayName}
          </h3>
        </div>

        {restaurant.description && (
          <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">
            {restaurant.description}
          </p>
        )}

        <div className="mt-3 flex items-center gap-2 text-xs font-medium text-gray-500 border-t border-gray-100 pt-3">
          <span className="bg-gray-100 px-2 py-1 rounded">
            {restaurant.openingDetails.text}
          </span>
        </div>
      </div>
    </div>
  );
};
